import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Training } from '../../services/firebase/training.service';
import { createFeedItemFromTraining } from '../../services/firebase/feed.service';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import logger from '../../utils/logger';
import { formatTrainingType } from '../../utils/adapters/training-adapters';

interface ShareTrainingProps {
  training: Training;
  onClose: () => void;
  onSuccess?: () => void;
}

const ShareTraining: React.FC<ShareTrainingProps> = ({ training, onClose, onSuccess }) => {
  const [isPublic, setIsPublic] = useState<boolean>(training.isPublic !== false);
  const [showOnFeed, setShowOnFeed] = useState<boolean>(training.shownOnFeed !== false);
  const [sharing, setSharing] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Reset states when training changes
    setIsPublic(training.isPublic !== false);
    setShowOnFeed(training.shownOnFeed !== false);
    setSuccess(false);
    setError(null);
  }, [training]);

  const handleShare = async () => {
    if (!user) {
      setError('You must be logged in to share a training session');
      return;
    }

    try {
      setSharing(true);
      setError(null);

      // First update the training visibility settings if needed
      if (training.isPublic !== isPublic || training.shownOnFeed !== showOnFeed) {
        const { updateTraining } = await import('../../services/firebase/training.service');
        await updateTraining(training.id, {
          isPublic,
          shownOnFeed: showOnFeed
        });
      }

      // Create feed item from training
      await createFeedItemFromTraining(training.id);

      setSuccess(true);
      setSharing(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error sharing training:', err);
      setError('Failed to share training session. Please try again.');
      setSharing(false);
    }
  };

  const handleNavigateToFeed = () => {
    navigate('/feed');
    onClose();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Share Training</h2>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mb-6">
        <h3 className="font-semibold mb-2 text-gray-700 dark:text-gray-200">{training.title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(training.date).toLocaleDateString()} • {training.totalTrainedTime} min • {formatTrainingType(training.type, t)}
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-700 dark:text-gray-200">Share on feed</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={showOnFeed}
              onChange={() => setShowOnFeed(!showOnFeed)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-700 dark:text-gray-200">Public (visible to everyone)</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={isPublic}
              onChange={() => setIsPublic(!isPublic)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {success ? (
        <div className="text-center">
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
            Training shared successfully!
          </div>
          <button
            onClick={handleNavigateToFeed}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-full"
          >
            Go to Feed
          </button>
        </div>
      ) : (
        <button
          onClick={handleShare}
          disabled={sharing || (!showOnFeed && !isPublic)}
          className={`px-6 py-2 rounded-md w-full transition-colors ${
            sharing 
              ? 'bg-gray-400 text-gray-700' 
              : !showOnFeed && !isPublic 
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {sharing ? 'Sharing...' : 'Share Training'}
        </button>
      )}
    </div>
  );
};

export default ShareTraining;